import { Button, Callout, Modal } from '@dev-spendesk/grapes';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useFeature } from 'common/hooks/useFeature';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { useUser } from 'modules/app/hooks/useUser';
import { getUserAuthenticationFactors } from 'modules/authentication-factor/api/getUserAuthenticationFactors';
import type {
  AuthenticationFactor,
  AuthenticationMethod,
} from 'modules/authentication-factor/models/authenticationFactors';
import { postShowPaymentPassword } from 'modules/cards/api/postShowPaymentPassword';
import { scaProcedureErrors } from 'modules/cards/models/sca';
import { AlternateAuthenticationMethodModal } from 'src/auth/modules/StrongCustomerAuthentication/components/AlternateAuthenticationMethodModal/AlternateAuthenticationMethodModal';
import { ModalMFAToken } from 'src/core/common/components/ModalMFA';
import FEATURES from 'src/core/constants/features';

import { usePostPaymentPasswordProcedure } from '../../hooks/usePostPaymentPasswordProcedure';
import { SfsUkPasswordModal } from '../SfsUkPasswordModal/SfsUkPasswordModal';
import mobileImage from '../assets/mobile.png';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const SFSUkScaPasswordModal = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation('global');

  const company = useCompany();
  const user = useUser();
  const hasTotpFeature = useFeature(FEATURES.TOTP);

  // dispatch is only injected in postShowPaymentPassword(...) because it's not
  // a React component, while we should not use useDispatch() inside a non-React
  // component.
  const dispatch = useDispatch();

  const [procedureId, setProcedureId] = useState<string | undefined>(undefined);
  const [alternateMethodProcedureId, setAlternateMethodProcedureId] = useState<
    string | undefined
  >(undefined);
  const [isAlternativeSmsOtpFlowOpen, setIsAlternativeSmsOtpFlowOpen] =
    useState(false);

  const [userAuthenticationFactors, setUserAuthenticationFactors] = useState<
    Array<AuthenticationFactor>
  >([]);

  const [
    showAlternateAuthenticationMethodModal,
    setShowAlternateAuthenticationMethodModal,
  ] = useState(false);

  const [selectedAuthenticationMethod, setSelectedAuthenticationMethod] =
    useState<AuthenticationMethod>('sca');

  const nonDefaultFactors = userAuthenticationFactors
    .filter((f) => !f.isDefault)
    .filter((f) => f.type !== 'EMAIL');

  const { passwordToShow, procedureFailedReason, setProcedureFailedReason } =
    usePostPaymentPasswordProcedure(
      {
        authenticationMethod: selectedAuthenticationMethod,
        procedureId,
      },
      undefined,
    );

  useEffect(() => {
    const retrieveUserAuthenticationFactors = async () => {
      if (hasTotpFeature) {
        const { factors } = await getUserAuthenticationFactors({
          companyId: company.id,
          userId: user.id,
        });
        setUserAuthenticationFactors(factors);
      }
    };

    retrieveUserAuthenticationFactors();
  }, []);

  useEffect(() => {
    const handleSCAShowPassword = async () => {
      try {
        const response = await postShowPaymentPassword(
          {
            authenticationMethod: selectedAuthenticationMethod,
          },
          dispatch,
        );

        // For the alternative SMS OTP flow, we don't want to set procedureId
        // right away as it would trigger the payment password fetching, while
        // we should do so only when the procedure is validated.
        if (selectedAuthenticationMethod === 'sca') {
          setProcedureId(response.procedureId);
        } else {
          setAlternateMethodProcedureId(response.procedureId);
        }
      } catch {
        setProcedureFailedReason('initializationError');
      }
    };

    handleSCAShowPassword();
    // When the user opens the alternative SMS OTP flow, we should create a new
    // procedure using the SMS fallback.
  }, [selectedAuthenticationMethod]);

  const handleAlternativeSmsOtpFlowClick = () => {
    setSelectedAuthenticationMethod('mfa');
    setIsAlternativeSmsOtpFlowOpen(true);
    // For erasing the context around the previous mobile app procedure and
    // stopping polling.
    setProcedureId(undefined);
  };

  const handleUseAlternateMethod = () => {
    if (nonDefaultFactors.length >= 1) {
      setShowAlternateAuthenticationMethodModal(true);
    } else {
      handleAlternativeSmsOtpFlowClick();
    }
  };

  const handleAlternateAuthenticationMethodModalClose =
    async (): Promise<void> => {
      onClose();
    };

  const handleAlternateAuthenticationMethodSelected = async (
    selectedAuthenticationFactor: AuthenticationFactor,
  ): Promise<void> => {
    let authenticationMethod: AuthenticationMethod;
    switch (selectedAuthenticationFactor.type) {
      case 'TOTP':
        authenticationMethod = 'totp';
        break;
      case 'SMS':
      case 'EMAIL':
        authenticationMethod = 'mfa';
        break;
      default:
        authenticationMethod = 'sca';
        break;
    }
    setProcedureId(undefined);
    setSelectedAuthenticationMethod(authenticationMethod);
  };

  const handleScaVerified = async (): Promise<void> => {
    setProcedureId(alternateMethodProcedureId);
  };

  if (passwordToShow) {
    // TODO: remove the filtering of SMS when support is added on backend.
    return (
      <SfsUkPasswordModal
        authenticationFactors={nonDefaultFactors.filter(
          (f) => f.type !== 'SMS',
        )}
        isOpen={isOpen}
        onClose={onClose}
        password={passwordToShow}
      />
    );
  }

  // FIXME: remove this flow when totp is in GA
  // I've decided to couple the SMS OTP fallback flow with the SCA through the
  // mobile app because they functionally are.
  if (isAlternativeSmsOtpFlowOpen) {
    return (
      // ModalMFAToken takes the procedure ID as stored by Redux, and its
      // storing is done inside postShowPaymentPassword(...).
      <ModalMFAToken
        // When the procedure is validated, we set its ID in procedureId's
        // value to trigger the right payment password fetching.
        onMfaTokenValidated={() => setProcedureId(alternateMethodProcedureId)}
        // Closing the SMS OTP modal also acts on closing this modal.
        onClose={onClose}
        isOpen
        flow="standard"
      />
    );
  }

  if (showAlternateAuthenticationMethodModal) {
    return (
      <AlternateAuthenticationMethodModal
        factors={nonDefaultFactors}
        flow="standard"
        isOpen
        onAlternateAuthenticationMethodSelected={
          handleAlternateAuthenticationMethodSelected
        }
        onClose={handleAlternateAuthenticationMethodModalClose}
        onScaVerified={handleScaVerified}
        operationId={alternateMethodProcedureId}
      />
    );
  }

  return (
    <Modal
      illustration={<img src={mobileImage} alt="" />}
      illustrationHeight="400px"
      title={t('cards.otpPasswordModal.title')}
      isOpen={isOpen}
      onClose={onClose}
      actions={[
        <Button
          key="cancel"
          onClick={onClose}
          text={t('cards.otpPasswordModal.cancel')}
          variant="secondaryNeutral"
        />,
      ]}
    >
      {procedureId && !procedureFailedReason && (
        <p className="-mt-16 mb-16">{t('cards.otpPasswordModal.subtitle')}</p>
      )}
      {procedureFailedReason && (
        <Callout
          className="mb-16"
          title={t(scaProcedureErrors[procedureFailedReason])}
          variant="alert"
        />
      )}
      <Button
        variant="tertiaryBrand"
        text={t('cards.otpPasswordModal.alternateMethodCta')}
        onClick={handleUseAlternateMethod}
      />
    </Modal>
  );
};
